import {
    SDKFunction,
    ScanType,
    SdkError,
    SdkType,
    ShareLinkOptions,
    ShareLinkResult
} from '../interface';
import { get, isNil, map, split, asyncLoadScript } from '@libs/shared';
import JsSdkBase from '../core/js-sdk-base';
import { jsSdkAuth, sdkLog, jsSdkFallback } from '../decorator';
import { sniffImageMimeByBinary, getImageFileName, sdkLogger } from '../utils';
import { UPLOAD_FILE_COUNT, JSSDK_URL, SdkResponseCode } from '../constant';

/**
 * 飞书sdk
 */
export default class LarkSdk extends JsSdkBase {
    constructor() {
        super();
        this.sdkType = SdkType.Lark;
    }

    /**
     * 加载飞书sdk脚本
     */
    @sdkLog()
    loadScript() {
        return asyncLoadScript(JSSDK_URL.Lark);
    }

    @sdkLog()
    init(config: Parameters<JsSdkBase['init']>[0]) {
        return super.init(config);
    }

    /**
     * 鉴权调用 JSAPI
     * @see https://open.feishu.cn/document/client-docs/h5/development-guide/step-2:-call-jsapi(optional)
     * @param config 飞书jssdk鉴权配置
     */
    @sdkLog()
    _config(config: JsSdkBase['jsSdkConfig']) {
        window.h5sdk?.config(config);
    }

    _ready(callback: () => void) {
        window.h5sdk?.ready(() => {
            sdkLogger.info(`${this.sdkType} ready callback`);
            callback();
        });
    }

    _error(callback: (err: any) => void) {
        window.h5sdk?.error((err: any) => {
            sdkLogger.error(`${this.sdkType} error callback`, { err });
            callback(err);
        });
    }

    /**
     * 预览图片
     * @see https://open.feishu.cn/document/client-docs/gadget/-web-app-api/media/image/previewimage
     * @param urls 图片地址列表，支持预览一组图片
     * @param current 默认显示的图片地址
     */
    @jsSdkAuth
    @sdkLog()
    previewImage(urls: string[], current: string) {
        return new Promise((resolve, reject) => {
            /**
             * 用于处理「垃圾飞书」hashchange会导致鉴权失效问题
             * 需要每次在调用功能前重新配置JSSDK
             */
            this.loadSdkConfig()
                .then(() => {
                    window.tt?.previewImage({
                        urls, // 图片地址列表
                        current, // 当前显示的图片链接
                        shouldShowSaveOption: true,
                        success: (result: any) => {
                            resolve(result);
                        },
                        fail: (err: any) => {
                            reject(
                                new SdkError(
                                    this.sdkType,
                                    SdkResponseCode.ThirdPartyServiceFail,
                                    JSON.stringify(err)
                                )
                            );
                        }
                    });
                })
                .catch((err) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                });
        });
    }

    /**
     * 获取设备当前的地理位置
     * @see https://open.feishu.cn/document/client-docs/gadget/-web-app-api/location/getlocation
     */
    @jsSdkAuth
    @jsSdkFallback((args) => [{ ...args[0], noIpLocate: 0 }])
    @sdkLog()
    getLocation(): ReturnType<JsSdkBase['getLocation']> {
        return new Promise((resolve, reject) => {
            this.loadSdkConfig()
                .then(() => {
                    window.tt?.getLocation({
                        type: 'gcj02',
                        success: (result: any) => {
                            const lng = result.longitude;
                            const lat = result.latitude;
                            if (!isNil(lng) && !isNil(lat)) {
                                const position = [lng, lat];
                                resolve([position, result]);
                            } else {
                                reject(
                                    new SdkError(
                                        this.sdkType,
                                        SdkResponseCode.Location.LngOrLatEmpty,
                                        `longitude or latitude is empty, longitude: ${lng}, latitude: ${lat}`
                                    )
                                );
                            }
                        },
                        fail: (err: any) => {
                            reject(
                                new SdkError(
                                    this.sdkType,
                                    SdkResponseCode.ThirdPartyServiceFail,
                                    JSON.stringify(err)
                                )
                            );
                        }
                    });
                })
                .catch((err) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                });
        });
    }

    /**
     * 扫码
     * @see https://open.feishu.cn/document/client-docs/gadget/-web-app-api/device/scan-code/scancode
     * @param options 扫码参数
     */
    @jsSdkAuth
    @sdkLog()
    scanCode(options: Parameters<JsSdkBase['scanCode']>[0]) {
        let scanType = ['qrCode', 'barCode'];
        switch (options.type) {
            case ScanType.QrCode:
                scanType = ['qrCode'];
                break;
            case ScanType.BarCode:
                scanType = ['barCode'];
                break;
        }
        return new Promise((resolve, reject) => {
            this.loadSdkConfig()
                .then(() => {
                    window.tt?.scanCode({
                        scanType,
                        success: (data: any) => {
                            resolve(get(data, 'result'));
                        },
                        fail: (err: any) => {
                            reject(
                                new SdkError(
                                    this.sdkType,
                                    SdkResponseCode.ThirdPartyServiceFail,
                                    JSON.stringify(err)
                                )
                            );
                        }
                    });
                })
                .catch((err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                });
        });
    }

    /**
     * 飞书分片读取文件
     * @param path 目标文件路径
     * @param total 文件总大小（单位：字节）
     * @param slice 分片大小（单位：字节）
     * @see https://open.feishu.cn/document/client-docs/gadget/-web-app-api/file/file_system_manager/file_system_manager_read_file#49506a84
     */
    @sdkLog()
    readFileBySlice(path: string, total: number, slice: number): Promise<Uint8Array> {
        // 分片文件总缓存
        const buffer = new Uint8Array(new ArrayBuffer(total));

        // 读取单个分片
        const readSingleSlice = (currentPosition: number): Promise<{ nextPosition: number }> => {
            return new Promise((resolve, reject) => {
                window.tt?.getFileSystemManager().readFile({
                    filePath: path,
                    position: currentPosition,
                    length: slice < total ? slice : total,
                    success: (res: { errMsg: string; data: ArrayBuffer }) => {
                        try {
                            const srcUint8 = new Uint8Array(res.data);
                            buffer.set(srcUint8, currentPosition);
                            const bufferByteLength = srcUint8.buffer.byteLength;
                            const nextPosition = currentPosition + bufferByteLength;

                            /**
                             * 正常来说，读取文件分片成功后，返回的 byteLength 值应该不为 0。
                             * 如果为 0，说明是异常情况，为防止无限循环，这里应该提前终止
                             * @see https://project.feishu.cn/b2rl2h/assignment/detail/5982454238
                             * @see https://project.feishu.cn/b2rl2h/assignment/detail/5979629897
                             */
                            if (bufferByteLength === 0) {
                                const message =
                                    'readFileSlice success callback, but byteLength is 0';
                                sdkLogger.warn(message, {
                                    res,
                                    currentPosition,
                                    nextPosition,
                                    bufferByteLength,
                                    total,
                                    slice
                                });
                                reject(
                                    new SdkError(
                                        this.sdkType,
                                        SdkResponseCode.H5JsApiError,
                                        message
                                    )
                                );
                                return;
                            }
                            resolve({ nextPosition });
                        } catch (error) {
                            reject(error);
                        }
                    },
                    fail: (err: any) => {
                        reject(err);
                    }
                });
            });
        };

        // 使用异步函数直接处理所有分片
        const readAllSlices = async (): Promise<Uint8Array> => {
            // 读取图片的初始位置为 0
            let currentPosition = 0;

            // 循环处理直到完成
            while (currentPosition < total) {
                // 等待当前分片处理完成
                const { nextPosition } = await readSingleSlice(currentPosition);
                currentPosition = nextPosition;
            }

            // 所有分片处理完成，返回结果
            return buffer;
        };
        return readAllSlices();
    }

    /**
     * 从系统相册中选择图片，或使用相机拍摄图片
     * @see https://open.feishu.cn/document/client-docs/gadget/-web-app-api/media/image/chooseimage
     * @param options 配置参数
     */
    @jsSdkAuth
    @sdkLog()
    chooseImage(options: Parameters<JsSdkBase['chooseImage']>[0]) {
        const {
            compressed = false,
            onlyCamera = false,
            maxFileCount = UPLOAD_FILE_COUNT.SINGLE
        } = options;

        return new Promise<File[]>((resolve, reject) => {
            const sizeType = compressed ? ['compressed'] : ['original'];
            const sourceType = onlyCamera ? ['camera'] : ['album', 'camera'];
            const count = Math.min(maxFileCount, 9);
            this.loadSdkConfig()
                .then(() => {
                    window.tt?.chooseImage({
                        count: count,
                        sizeType: sizeType, // 可以指定是原图还是压缩图，默认二者都有
                        sourceType: sourceType, // 可以指定来源是相册还是相机，默认二者都有
                        success: (res: any) => {
                            const files: any[] = [];
                            const readFiles = map(res.tempFiles, ({ path, size }) => {
                                return new Promise((resolve, reject) => {
                                    const slice = 1024 * 1024; // 分片1M
                                    const name = split(path, '/').pop() || '';
                                    sdkLogger.info('lark chooseImage tempFiles', {
                                        path,
                                        name
                                    });

                                    this.readFileBySlice(path, size, slice)
                                        .then((res) => {
                                            const { mime, ext } = sniffImageMimeByBinary(res);
                                            const type = mime ?? 'image/jpeg';
                                            const blob = new Blob([res as any], {
                                                type: type
                                            });

                                            const fileName = getImageFileName(name, ext);
                                            sdkLogger.info(
                                                'lark sniffImageMime after readFileBySlice',
                                                {
                                                    type,
                                                    mime,
                                                    sniffedExt: ext,
                                                    fileName
                                                }
                                            );
                                            files.push(
                                                new File([blob], fileName, {
                                                    type: type,
                                                    lastModified: new Date().getTime()
                                                })
                                            );
                                            resolve(null);
                                        })
                                        .catch((err) => {
                                            reject(
                                                new SdkError(
                                                    this.sdkType,
                                                    SdkResponseCode.ThirdPartyServiceFail,
                                                    JSON.stringify(err)
                                                )
                                            );
                                        });
                                });
                            });
                            Promise.all(readFiles).then(() => {
                                resolve(files);
                            }, reject);
                        },
                        fail: (err: any) => {
                            reject(
                                new SdkError(
                                    this.sdkType,
                                    SdkResponseCode.ThirdPartyServiceFail,
                                    JSON.stringify(err)
                                )
                            );
                        }
                    });
                })
                .catch((err) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                });
        });
    }

    /**
     * 飞书设置网页标题
     * @param title 网页标题
     * 注意，这里不能用两段式API：tt.setNavigationBarTitle，因为该两段式API仅支持小程序环境使用
     * 同时注意，当前使用的三段式API：h5sdk.biz.navigation.setTitle，没有返回结果
     * @see https://project.feishu.cn/b2rl2h/assignment/detail/6130596641
     */
    @jsSdkAuth
    @sdkLog()
    setPageTitle(title: string) {
        window.h5sdk?.biz?.navigation?.setTitle?.({ title });
        return Promise.resolve();
    }

    /**
     * 跳转到小程序以外的应用
     * @see https://open.larkoffice.com/document/client-docs/gadget/-web-app-api/navigation/openschema
     * @param url 目标地址
     */
    @jsSdkAuth
    @jsSdkFallback()
    @sdkLog()
    openLink(url: string) {
        return new Promise((resolve, reject) => {
            window.tt?.openSchema({
                schema: url,
                external: true,
                success: (res: any) => {
                    resolve(res);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 关闭当前窗口
     * @see https://open.feishu.cn/document/client-docs/gadget/-web-app-api/navigation/closewindow
     */
    @jsSdkAuth
    @sdkLog()
    closeWindow() {
        return new Promise<void>((resolve, reject) => {
            window.tt?.closeWindow({
                success: (res: any) => {
                    resolve(res);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 飞书应用授权
     * @see https://open.feishu.cn/document/client-docs/gadget/-web-app-api/open-ability/authorize/applytenantappscope
     */
    @sdkLog()
    applyAppScope() {
        return new Promise((resolve, reject) => {
            this.loadSdkConfig()
                .then(() => {
                    window.tt?.applyTenantAppScope({
                        success: (res: any) => {
                            resolve(res);
                        },
                        fail: (err: any) => {
                            reject(
                                new SdkError(
                                    this.sdkType,
                                    SdkResponseCode.ThirdPartyServiceFail,
                                    JSON.stringify(err)
                                )
                            );
                        }
                    });
                })
                .catch((err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                });
        });
    }

    hasFunction(functionName: SDKFunction): Promise<boolean> {
        const functionList = [SDKFunction.ChooseImage];
        return Promise.resolve(functionList.includes(functionName));
    }

    /**
     * 飞书自定义转发到会话
     * PC 完成分享时 success 返回 code: 0，识别为「转发成功」
     * Mobile 唤起分享组件即触发 success 回调，所以无法区分「转发成功」与「取消」
     * @see https://open.feishu.cn/document/client-docs/gadget/-web-app-api/open-ability/share/sharewebcontent
     */
    @jsSdkAuth
    @sdkLog()
    shareLink(options: ShareLinkOptions): Promise<ShareLinkResult | void> {
        // 页面 hash 变化后需要重新鉴权。
        return this.loadSdkConfig()
            .then(
                () =>
                    new Promise<ShareLinkResult | void>((resolve, reject) => {
                        window.tt?.shareWebContent({
                            url: options.url,
                            success: (res: { code?: number; errMsg?: string }) => {
                                if (res.code === 0) {
                                    resolve({ status: 'success', data: res });
                                } else {
                                    resolve({ data: res });
                                }
                            },
                            fail: reject
                        });
                    })
            )
            .catch((err) => {
                throw new SdkError(
                    this.sdkType,
                    SdkResponseCode.ThirdPartyServiceFail,
                    JSON.stringify(err)
                );
            });
    }
}
